/**
 * Local mock data for the Insights pages, used when environment.useMockData is true.
 * Mirrors the design content so the tabs can be reviewed without the API.
 */
import {
  InsightsAuditEntry,
  InsightsFailure,
  InsightsOverview,
  InsightsTester,
} from '../models/insights';

export const mockInsightsFailures: InsightsFailure[] = [
  { testCaseId: 1, testCaseName: 'Clinical Summary labs not rendering in BOLD for Ambulatory', planName: 'Clinical Summary', severity: 'Showstopper', reporterUserId: 1, reporterName: 'Jennifer Martin', status: 'Open', filedOnUtc: '2026-07-13T15:42:00Z' },
  { testCaseId: 2, testCaseName: 'Session expires after 15 minutes of idle time', planName: 'Login Module', severity: 'Blocker', reporterUserId: 1, reporterName: 'Jennifer Martin', status: 'Review', filedOnUtc: '2026-07-11T09:10:00Z' },
  { testCaseId: 3, testCaseName: 'Patient lookup by partial MRN returns wrong results', planName: 'Patient Lookup & Search', severity: 'Blocker', reporterUserId: 2, reporterName: 'Marcus Chen', status: 'Open', filedOnUtc: '2026-07-13T11:20:00Z' },
  { testCaseId: 4, testCaseName: 'Duplicate patient records returned when DOB matches', planName: 'Patient Lookup & Search', severity: 'Blocker', reporterUserId: 2, reporterName: 'Marcus Chen', status: 'Open', filedOnUtc: '2026-07-13T10:18:00Z' },
  { testCaseId: 5, testCaseName: 'Recurring appointment conflicts with blocked time', planName: 'Recurring Appointments', severity: 'Functional', reporterUserId: 4, reporterName: 'Liana Garcia', status: 'Open', filedOnUtc: '2026-07-12T16:52:00Z' },
  { testCaseId: 6, testCaseName: 'Insurance card upload fails on files larger than 5MB', planName: 'Demographics & Insurance', severity: 'Functional', reporterUserId: 5, reporterName: 'Robert Walsh', status: 'Review', filedOnUtc: '2026-07-12T13:05:00Z' },
  { testCaseId: 7, testCaseName: 'Appointment time-zone shifts by one hour on save', planName: 'Appointment Booking Flow', severity: 'Functional', reporterUserId: 3, reporterName: 'Jasmine Patel', status: 'Open', filedOnUtc: '2026-07-13T08:40:00Z' },
  { testCaseId: 8, testCaseName: "Sign out from avatar menu doesn't clear localStorage", planName: 'Login Module', severity: 'Cosmetic', reporterUserId: 1, reporterName: 'Jennifer Martin', status: 'Open', filedOnUtc: '2026-07-10T14:30:00Z' },
];

export const mockInsightsTesters: InsightsTester[] = [
  {
    userId: 1, firstName: 'Jennifer', lastName: 'Martin', isActive: true,
    currentPlanName: 'Login Module - Desktop', currentTestNumber: 12, currentTestTotal: 47,
    completedCount: 18, passRatePct: 94.4, failuresFiled: 1,
    plans: [
      { planName: 'Login Module - Desktop', isCurrent: true, numberOfPassingTests: 12, numberOfFailingTests: 2, numberOfTests: 47 },
      { planName: 'Login Module - Mobile', isCurrent: false, numberOfPassingTests: 23, numberOfFailingTests: 0, numberOfTests: 23 },
    ],
  },
  {
    userId: 2, firstName: 'Marcus', lastName: 'Chen', isActive: true,
    currentPlanName: 'Patient Lookup & Search', currentTestNumber: 26, currentTestTotal: 31,
    completedCount: 21, passRatePct: 87.5, failuresFiled: 3,
    plans: [
      { planName: 'Patient Lookup & Search', isCurrent: true, numberOfPassingTests: 22, numberOfFailingTests: 4, numberOfTests: 31 },
      { planName: 'Encounter History', isCurrent: false, numberOfPassingTests: 0, numberOfFailingTests: 0, numberOfTests: 15 },
    ],
  },
  {
    userId: 3, firstName: 'Jasmine', lastName: 'Patel', isActive: true,
    currentPlanName: 'Appointment Booking Flow', currentTestNumber: 22, currentTestTotal: 33,
    completedCount: 14, passRatePct: 100, failuresFiled: 0,
    plans: [
      { planName: 'Appointment Booking Flow', isCurrent: true, numberOfPassingTests: 22, numberOfFailingTests: 0, numberOfTests: 33 },
      { planName: 'SSO & Federation', isCurrent: false, numberOfPassingTests: 0, numberOfFailingTests: 0, numberOfTests: 18 },
    ],
  },
  {
    userId: 4, firstName: 'Liana', lastName: 'Garcia', isActive: true,
    currentPlanName: 'Recurring Appointments', currentTestNumber: 18, currentTestTotal: 28,
    completedCount: 17, passRatePct: 94.4, failuresFiled: 1,
    plans: [
      { planName: 'Recurring Appointments', isCurrent: true, numberOfPassingTests: 17, numberOfFailingTests: 1, numberOfTests: 28 },
      { planName: 'Provider Calendar Sync', isCurrent: false, numberOfPassingTests: 0, numberOfFailingTests: 0, numberOfTests: 17 },
    ],
  },
  {
    userId: 5, firstName: 'Robert', lastName: 'Walsh', isActive: false,
    currentPlanName: 'Demographics & Insurance Edit', currentTestNumber: 12, currentTestTotal: 24,
    completedCount: 12, passRatePct: 72.7, failuresFiled: 3,
    plans: [
      { planName: 'Demographics & Insurance Edit', isCurrent: true, numberOfPassingTests: 12, numberOfFailingTests: 3, numberOfTests: 24 },
    ],
  },
];

export const mockInsightsAudit: InsightsAuditEntry[] = [
  { timeUtc: '2026-07-13T15:42:00Z', userId: 1, userName: 'Jennifer Martin', actionType: 'Failed', itemName: 'Clinical Summary labs not rendering in BOLD', planName: 'Clinical Summary', severity: 'Showstopper', detail: 'Labs render in regular weight on Ambulatory' },
  { timeUtc: '2026-07-13T15:38:00Z', userId: 1, userName: 'Jennifer Martin', actionType: 'Passed', itemName: 'Reset password via email link', planName: 'Login Module - Desktop', severity: null, detail: null },
  { timeUtc: '2026-07-13T15:24:00Z', userId: 3, userName: 'Jasmine Patel', actionType: 'Passed', itemName: 'Book single appointment', planName: 'Appointment Booking Flow', severity: null, detail: null },
  { timeUtc: '2026-07-13T15:18:00Z', userId: 2, userName: 'Marcus Chen', actionType: 'Failed', itemName: 'Duplicate patient records returned when DOB matches', planName: 'Patient Lookup & Search', severity: 'Blocker', detail: null },
  { timeUtc: '2026-07-13T15:02:00Z', userId: 4, userName: 'Liana Garcia', actionType: 'Passed', itemName: 'Edit single instance of a series', planName: 'Recurring Appointments', severity: null, detail: null },
  { timeUtc: '2026-07-12T16:52:00Z', userId: 4, userName: 'Liana Garcia', actionType: 'Failed', itemName: 'Recurring appointment conflicts with blocked time', planName: 'Recurring Appointments', severity: 'Functional', detail: null },
  { timeUtc: '2026-07-12T16:21:00Z', userId: 1, userName: 'Jennifer Martin', actionType: 'Passed', itemName: 'Tap-to-sign-in on iPad in portrait', planName: 'Login Module - Mobile', severity: null, detail: null },
  { timeUtc: '2026-07-12T15:18:00Z', userId: 3, userName: 'Jasmine Patel', actionType: 'Failed', itemName: "Cancel button doesn't close booking dialog", planName: 'Appointment Booking Flow', severity: 'Functional', detail: null },
  // Non-result events (PBI 57764). These have been written to the audit table all
  // along and were never displayed; they exercise the Skips, Assignments and
  // Visibility chips in local dev.
  { timeUtc: '2026-07-13T15:30:00Z', userId: 2, userName: 'Marcus Chen', actionType: 'Skipped', itemName: 'Verify SSO redirect for legacy tenants', planName: 'Login Module - Desktop', severity: null, detail: null },
  { timeUtc: '2026-07-13T09:05:00Z', userId: 1, userName: 'Jennifer Martin', actionType: 'Reassigned', itemName: 'Patient Lookup & Search', planName: 'Patient Lookup & Search', severity: null, detail: 'Marcus Chen' },
  { timeUtc: '2026-07-13T08:58:00Z', userId: 1, userName: 'Jennifer Martin', actionType: 'Assigned', itemName: 'Recurring Appointments', planName: 'Recurring Appointments', severity: null, detail: 'Liana Garcia' },
  { timeUtc: '2026-07-12T17:40:00Z', userId: 1, userName: 'Jennifer Martin', actionType: 'Unassigned', itemName: 'Clinical Summary', planName: 'Clinical Summary', severity: null, detail: null },
  { timeUtc: '2026-07-12T08:30:00Z', userId: 1, userName: 'Jennifer Martin', actionType: 'PlanSetLive', itemName: 'Appointment Booking Flow', planName: 'Appointment Booking Flow', severity: null, detail: null },
  { timeUtc: '2026-07-11T17:12:00Z', userId: 1, userName: 'Jennifer Martin', actionType: 'TestCaseSetNotLive', itemName: 'Legacy print layout', planName: 'Clinical Summary', severity: null, detail: null },
];

export const mockInsightsOverview: InsightsOverview = {
  overallProgressPct: 54,
  testsComplete: 183,
  testsTotal: 340,
  passRatePct: 93.4,
  passed: 171,
  run: 183,
  activeTesters: 8,
  totalTesters: 11,
  unassignedPlans: 3,
  openFailures: mockInsightsFailures.length,
  allFailures: mockInsightsFailures,
  testerActivity: mockInsightsTesters.map((t) => ({
    userId: t.userId,
    firstName: t.firstName,
    lastName: t.lastName,
    completedCount: t.completedCount,
    recentCompleted: t.plans
      .filter((p) => p.numberOfPassingTests > 0)
      .slice(0, 3)
      .map((p) => ({ testCaseName: `Verified ${p.planName}`, planName: p.planName, isPass: true })),
  })),
};
